/**
 * AdminDashboard — refreshed admin landing composed entirely from
 * the (m.6) primitives.
 *
 * IA (PR (m.7) Phase 2):
 *
 *   1. PageHeader (Administrative Overview eyebrow + Bavex title)
 *   2. Countdown hero (anchor event from useCountdownAnchor) — same
 *      treatment as the contestant dashboard so directors and
 *      contestants share a unified pageant-day surface
 *   3. KPI strip — Total / Submitted / Approved / Pending Titles /
 *      Pending Costumes
 *   4. Application Pipeline ChartCard (horizontal bar) — Draft →
 *      Submitted → Approved → Rejected counts derived from
 *      `useAdminDashboard.pipeline`. Click-through to the Applicants
 *      list with the matching status filter.
 *   5. Upcoming Events + Recent Inquiries row
 *   6. Recent Activity placeholder (audit-log integration deferred
 *      to a follow-up since the audit-log fetch lives in
 *      ActivityLog.tsx and is out of scope for this PR)
 *
 * Spec: meridian-redesign — PR (m.7) Phase 2
 * Requirements: 1.5, 8.1, 8.2, 8.3, 14.1, 18.5
 */

import { useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  Clock,
  Crown,
  FileCheck,
  Mail,
  MapPin,
  MessageSquare,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { useAdminDashboard, type AdminDashboardData } from '@/hooks/useAdminDashboard';
import { useAuth } from '@/hooks/useAuth';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { useCountdownAnchor } from '@/hooks/useCountdownAnchor';
import { formatDeadlineLocal } from '@/lib/phaseDeadlineFormat';
import { shortenAddress } from '@/utils/shortenAddress';

import { PageHeader } from '@/components/ui/page-header';
import { KpiTile } from '@/components/ui/kpi-tile';
import { ChartCard } from '@/components/ui/chart-card';
import { Countdown } from '@/components/ui/countdown';

interface KpiDescriptor {
  label: string;
  value: number;
  icon: LucideIcon;
  link: string;
  variant?: 'default' | 'tinted' | 'alert';
}

export interface AdminDashboardProps {
  /**
   * Optional pre-fetched dashboard data — used by the dev preview
   * at `/dev/dashboards-preview` to mount the dashboard without a
   * live AWS session. Production usage omits this prop and the
   * live hook fetches the real data.
   */
  previewData?: AdminDashboardData;
  /**
   * Optional override for the countdown anchor. The preview page
   * supplies a deterministic anchor so the rendered dashboard is
   * stable across reloads. Mirrors `ApplicantDashboardProps`.
   */
  previewCountdownDate?: string;
}

export function AdminDashboard(props: AdminDashboardProps = {}): JSX.Element {
  const { role } = useAuth();
  // Season SSOT: operative competition year + active Season row from the
  // active-season seam. The Season carries the two phase deadlines
  // (Phase_1_Close_Date = intakeWindow.closesAt, Phase_2_Due_Date =
  // phaseTwoDueDate) surfaced below (season-phase-deadlines, R14).
  const { season, operativeYear } = useActiveSeason();
  const liveDashboard = useAdminDashboard();
  const liveAnchor = useCountdownAnchor(operativeYear);

  const data = props.previewData ?? liveDashboard.data;
  const loading = props.previewData ? false : liveDashboard.loading;
  const error = props.previewData ? null : liveDashboard.error;
  const countdownAnchor = props.previewCountdownDate
    ? { date: props.previewCountdownDate, title: 'Pageant Day' }
    : liveAnchor.anchor;

  // Season phase deadlines, rendered in the Viewer_Local_Timezone via the
  // shared formatter. Each returns '' when the underlying instant is
  // absent/null, so the corresponding row is omitted (R14.1–R14.5).
  const phaseOneCloseLocal = formatDeadlineLocal(season?.intakeWindow?.closesAt, {
    withZone: true,
  });
  const phaseTwoDueLocal = formatDeadlineLocal(season?.phaseTwoDueDate, {
    withZone: true,
  });

  const pipelineSegments = useMemo(() => {
    if (!data) return [];
    return [
      { id: 'draft',     label: 'Draft',    count: data.pipeline.draft,     textClass: 'text-muted-foreground', fill: 'var(--muted-foreground)' },
      { id: 'submitted', label: 'Pending',  count: data.pipeline.submitted, textClass: 'text-info-text',        fill: 'var(--info)'             },
      { id: 'approved',  label: 'Approved', count: data.pipeline.approved,  textClass: 'text-success-text',     fill: 'var(--success)'          },
      { id: 'rejected',  label: 'Rejected', count: data.pipeline.rejected,  textClass: 'text-destructive-text', fill: 'var(--destructive)'      },
    ];
  }, [data]);

  const pipelineTotal = pipelineSegments.reduce((acc, s) => acc + s.count, 0);
  const pipelineDonutData = pipelineSegments.filter((s) => s.count > 0);

  if (loading) {
    return (
      <div
        className="text-center py-16 px-5"
        data-testid="admin-dashboard-loading"
      >
        <p className="text-muted-foreground text-lg">Loading the command center…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="text-center py-16 px-5"
        data-testid="admin-dashboard-error"
      >
        <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4 inline-block">
          <p>Failed to load admin dashboard. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  const { applicants, recentInquiries, upcomingEvents, kpis } = data!;

  const kpiTiles: KpiDescriptor[] = [
    {
      label: 'Total Applications',
      value: applicants.length,
      icon: Users,
      link: '/admin/approvals',
      variant: 'tinted',
    },
    {
      label: 'Submitted',
      value: kpis.submitted,
      icon: AlertCircle,
      link: '/admin/approvals?status=SUBMITTED',
      variant: kpis.submitted > 0 ? 'alert' : 'default',
    },
    {
      label: 'Approved',
      value: kpis.approved,
      icon: FileCheck,
      link: '/admin/approvals?status=APPROVED',
    },
    {
      label: 'Pending Titles',
      value: kpis.pendingTitles,
      icon: Crown,
      link: '/admin/approvals?tab=titles',
      variant: kpis.pendingTitles > 0 ? 'alert' : 'default',
    },
  ];

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <PageHeader
        size="lg"
        action={
          role === 'poweruser' ? (
            <Link
              to="/admin/participants"
              className="text-eyebrow-sm bg-card border border-border rounded-card hover:border-primary transition-colors px-5 py-3 min-h-[var(--touch-target-min)] no-underline inline-flex items-center gap-3"
              data-testid="poweruser-shortcut"
            >
              <FileCheck className="w-4 h-4 text-muted-foreground" />
              <span>Approved Contestants · {kpis.approved}</span>
            </Link>
          ) : null
        }
      >
        Dashboard
      </PageHeader>

      {/* Countdown hero — matches the contestant dashboard treatment so
          both surfaces share the same hero rhythm; mounts only when an
          anchor exists for the season (R8.5 countdown-anchor). */}
      {countdownAnchor?.date ? (
        <Countdown
          targetDate={countdownAnchor.date}
          eyebrow={countdownAnchor.title || 'Pageant Day'}
        />
      ) : null}

      {/* Season phase deadlines — Phase_1_Close_Date and Phase_2_Due_Date
          shown in the viewer's local zone, each omitted when absent
          (season-phase-deadlines, R14). */}
      {phaseOneCloseLocal || phaseTwoDueLocal ? (
        <div className="flex flex-col gap-4" data-testid="admin-phase-deadlines">
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-display text-page-title text-foreground">
              Season Deadlines
            </h3>
          </div>
          {/* Each deadline renders as its own pageant-style Countdown card
              (eyebrow + friendly date + live number row), stacked one per row. */}
          {season?.intakeWindow?.closesAt && phaseOneCloseLocal ? (
            <Countdown
              targetDate={season.intakeWindow.closesAt}
              eyebrow="Phase 1 Close"
              pastEyebrow="Phase 1 Closed"
              data-testid="admin-phase-one-close"
            />
          ) : null}
          {season?.phaseTwoDueDate && phaseTwoDueLocal ? (
            <Countdown
              targetDate={season.phaseTwoDueDate}
              eyebrow="Phase 2 Due"
              pastEyebrow="Phase 2 Passed"
              data-testid="admin-phase-two-due"
            />
          ) : null}
        </div>
      ) : null}

      {/* KPI strip */}
      {role === 'admin' || role === 'poweruser' ? (
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="admin-dashboard-kpis"
        >
          {kpiTiles.map((tile) => (
            <Link
              key={tile.label}
              to={tile.link}
              className="no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-card"
            >
              <KpiTile
                label={tile.label}
                value={tile.value}
                variant={tile.variant ?? 'default'}
                data-testid={`admin-kpi-${tile.label.toLowerCase().replace(/\s+/g, '-')}`}
              />
            </Link>
          ))}
        </div>
      ) : null}

      {/* Application pipeline + Upcoming events row */}
      {pipelineSegments.length > 0 && applicants.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard eyebrow="Application Pipeline">
            <div
              data-testid="meridian-admin-pipeline-donut"
              className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-6 items-center justify-items-center sm:justify-items-start mx-auto sm:max-w-md sm:mx-auto"
            >
              {/* Donut chart with center readout */}
              <div className="relative h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pipelineDonutData}
                      dataKey="count"
                      nameKey="label"
                      innerRadius="62%"
                      outerRadius="92%"
                      paddingAngle={pipelineDonutData.length > 1 ? 2 : 0}
                      startAngle={90}
                      endAngle={-270}
                      isAnimationActive={false}
                      stroke="var(--card)"
                      strokeWidth={2}
                    >
                      {pipelineDonutData.map((seg) => (
                        <Cell
                          key={seg.id}
                          fill={seg.fill}
                          data-testid={`pipeline-segment-${seg.id}`}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-input)',
                        fontSize: 12,
                        padding: '0.5rem 0.75rem',
                      }}
                      formatter={(value: number, name: string) => [
                        `${value} (${pipelineTotal > 0 ? Math.round((Number(value) / pipelineTotal) * 100) : 0}%)`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center readout */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="font-display text-3xl text-foreground italic leading-none">
                    {pipelineTotal}
                  </span>
                  <span className="text-eyebrow-xs text-muted-foreground mt-1">
                    Total
                  </span>
                </div>
              </div>

              {/* Legend — count + label, no swatch, no percentages */}
              <div className="flex flex-col gap-3">
                {pipelineSegments.map((seg) => (
                  <div
                    key={seg.id}
                    data-testid={`pipeline-legend-${seg.id}`}
                    className="flex items-baseline gap-3"
                  >
                    <span className={`font-display text-2xl italic tabular-nums leading-none ${seg.textClass}`}>
                      {seg.count}
                    </span>
                    <span className="text-eyebrow-sm text-muted-foreground">
                      {seg.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>
          <UpcomingEventsPanel events={upcomingEvents} />
        </div>
      ) : null}

      {/* Podium + Recent Inquiries row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PodiumPanel />
        <InquiriesPanel inquiries={recentInquiries} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-panels
// ---------------------------------------------------------------------------

function UpcomingEventsPanel({
  events,
}: {
  events: AdminDashboardData['upcomingEvents'];
}): JSX.Element {
  return (
    <div
      className="bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8"
      data-testid="admin-upcoming-events"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-page-title text-foreground">
            Upcoming Events
          </h3>
        </div>
        <Link
          to="/admin/calendar"
          className="text-eyebrow-sm text-primary hover:text-foreground transition-colors no-underline touch-target"
        >
          View Calendar
        </Link>
      </div>
      {events.length === 0 ? (
        <p className="text-eyebrow-sm text-muted-foreground">
          No upcoming events scheduled.
        </p>
      ) : (
        <div className="space-y-4">
          {events.slice(0, 3).map((event) => (
            <Link
              key={event.id}
              to={`/admin/calendar?event=${event.id}&month=${event.date.slice(0, 7)}`}
              className="flex items-start gap-4 no-underline -mx-2 px-2 py-2 rounded transition-colors hover:bg-muted/40"
            >
              <div className="flex flex-col items-center min-w-[44px]">
                <span className="text-kpi-sm text-foreground">
                  {new Date(event.date + 'T00:00:00').getDate()}
                </span>
                <span className="text-eyebrow-sm text-muted-foreground">
                  {new Date(event.date + 'T00:00:00').toLocaleDateString(
                    'default',
                    { month: 'short' },
                  )}
                </span>
              </div>
              <div className="min-w-0">
                <p className="font-sans text-sm text-foreground font-medium truncate">
                  {event.title}
                </p>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-eyebrow-sm text-muted-foreground inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {event.time}
                  </span>
                  {event.location ? (
                    <span className="text-eyebrow-sm text-muted-foreground inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {shortenAddress(event.location)}
                    </span>
                  ) : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function InquiriesPanel({
  inquiries,
}: {
  inquiries: AdminDashboardData['recentInquiries'];
}): JSX.Element {
  return (
    <div
      className="bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8"
      data-testid="admin-inquiries-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-page-title text-foreground">
            Inquiries
          </h3>
        </div>
        <Link
          to="/admin/messages?tab=inquiries"
          className="text-eyebrow-sm text-primary hover:text-foreground transition-colors no-underline touch-target"
        >
          View All
        </Link>
      </div>
      {inquiries.length === 0 ? (
        <p className="text-eyebrow-sm text-muted-foreground">
          No recent inquiries.
        </p>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inq) => (
            <Link
              key={inq.id}
              to={`/admin/messages?tab=inquiries&inquiry=${inq.email}`}
              className="flex items-center gap-3 rounded-card -mx-2 px-2 py-2 no-underline hover:bg-muted/40 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-sans text-sm text-foreground truncate">
                  {inq.name}
                </p>
                <p className="text-eyebrow-sm text-muted-foreground truncate">
                  {inq.subject}
                </p>
              </div>
              <span className="text-eyebrow-sm text-muted-foreground shrink-0">
                {inq.createdAt
                  ? new Date(inq.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                  : ''}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PodiumPanel — surfaces the most recent Stream Chat messages and
 * received reactions on the admin dashboard. Each row is clickable
 * and routes to the corresponding chat thread.
 *
 * Current state: this panel renders a placeholder empty state.
 * Stream Chat doesn't expose a "recent messages across all
 * channels" query that's cheap from the admin dashboard's data
 * shape, so wiring the live message feed needs a backend index
 * (or an opinionated channel list to scan). That work is tracked
 * separately. The component shipped here is the visual placement
 * the user requested — left of Inquiries, same chrome as
 * UpcomingEventsPanel / InquiriesPanel — so the dashboard layout
 * is correct now and the data wiring can land in a follow-up
 * without touching the layout again.
 */
function PodiumPanel(): JSX.Element {
  return (
    <div
      className="bg-card border border-border rounded-card shadow-subtle p-6 sm:p-8"
      data-testid="admin-podium-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-page-title text-foreground">
            Podium
          </h3>
        </div>
        <Link
          to="/admin/messages"
          className="text-eyebrow-sm text-primary hover:text-foreground transition-colors no-underline touch-target"
        >
          Open Podium
        </Link>
      </div>
      <p className="text-eyebrow-sm text-muted-foreground">
        Recent messages and reactions will surface here once your Podium
        workspace has activity.
      </p>
    </div>
  );
}

export default AdminDashboard;
